import React, { useState, useEffect, useRef } from "react";
import Button from "../../components/common/ui/Button";
import {
  PasswordInput,
  SelectInput,
  TextInput,
} from "../../components/common/ui/Inputs";
import { useDispatch, useSelector } from "react-redux";
import formatPhoneNumber from "./../../_helpers/formatPhoneNumber";
import { history } from "./../../_helpers/history";
import { managementUserAction, authActions } from "./../../_store";
import { masterActions } from "./../../_store/master.slice";
import managementUserSchema from "../../validation/managementUserSchema";
import RadioButtonGroup from "../../components/common/ui/Inputs/RadioInput";
import { decryptData, encryptData } from "../../utils/encryptDecrypt";
import FacilityAdminMenuAccess from "./FacilityAdminMenuAccess";
import ManagementUserForm from "./ManagementUserForm";
import { showToastNotification } from "../../components/common/ui/NotificationModal/ToastNotication";
import { StoredProcedures, MenuItem } from "../../_helpers/enumerator";

function AddManagementUser() {
  // Get necessary state values from Redux

  const { Role, PsotalMailGrid, managementUserData, managementUserInfo } =
    useSelector((state) => state.managementUser); // Role and postal mail grid from managementUser state

  // const { Status } = useSelector((state) => state.dropdownOptions); // Dropdown options status

  const { user, commonFacilityCode: FacilityID } = useSelector((x) => x.auth); // Get current authenticated user

  const dispatch = useDispatch(); // Initializing dispatch function for dispatching actions

  useEffect(() => {
    dispatch(
      masterActions.LoadFacilityDropDown({
        facilityID: 0,
        activeStatus: 1,
      })
    );
  }, [dispatch]);

  // Load facility dropdown on component mount
  const prevFacilityCodeRef = useRef(FacilityID);

  useEffect(() => {
    if (prevFacilityCodeRef.current !== FacilityID) {
      history.navigate("ManagementUserList");
    }
  }, [FacilityID]);

  /**
   * First useEffect:
   * This runs when the component mounts to fetch user roles and state list.
   * It only runs once due to the empty dependency array.
   */
  useEffect(() => {
    dispatch(managementUserAction.GetUserRole()); // Dispatch action to get user roles
    dispatch(masterActions.GetStateList()); // Dispatch action to get state list for dropdown
  }, [dispatch]); // Empty dependency array ensures this runs only once on component mount

  //Back go back to the ManagementList
  const handleBack = () => {
    dispatch(managementUserAction.resetForm());
    dispatch(managementUserAction.resetMenuList());
    history.navigate("/Admin/ManagementUserList");
  };

  const [showExtraFeild, setShowExtraFeild] = useState(false); // Toggle visibility for extra fields (PDS Permission, isAllowtoAccessOthersPDForm)
  const [errorMessage, setErrorMessage] = useState({}); // Local state for managing form error messages

  let addUser = { ...managementUserData };

  // JOI validation function
  const validate = async () => {
    try {
      await managementUserSchema.validateAsync(managementUserData, {
        abortEarly: false,
      });
      return {};
    } catch (err) {
      const validationErrors = {};
      err.details.forEach((error) => {
        const field = error.path[0]; // Get the actual field name from the error path
        validationErrors[field] = error.message; // Use the correct field name
      });
      return validationErrors;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await validate();
    setErrorMessage(validationErrors);
    if (Object.keys(validationErrors).length !== 0) {
      return;
    }

    const encryptPassword = encryptData(addUser?.UserPassword);

    addUser.UserPassword = encryptPassword;
    addUser.FacilityID = FacilityID || 0;
    addUser.CreatedBy = user?.ManagementUserID;
    addUser.type_ManagementUserMenuPermission =
      addUser?.type_ManagementUserMenuPermission?.filter((menu) => {
        // Define allowed roles for each menu
        const allowedRoles = {
          //"Management Users"
          [MenuItem.UserMenuItem]: [12],
          //Dashboard
          [MenuItem.DashboardMenuItem]: [2, 3, 12, 13],
          //Documents
          [MenuItem.DocumentsMenuItem]: [2, 12],
          //"Postal Mail"
          [MenuItem.PostalMailMenuItem]: [2, 3, 6, 12, 13],
          // Lockdown
          [MenuItem.LockdownMenuItem]: [2, 12],
          // Inmates
          [MenuItem.InmatesMenuItem]: [2, 6, 12, 13],
          // "Public Users"
          [MenuItem.PublicUsersMenuItem]: [2, 6, 12],
          // "Staff Request"
          [MenuItem.RequestsOrGrievancesMenuItem]: [2, 3, 6, 12, 13],
          //Messages
          [MenuItem.MessagesMenuItem]: [2, 3, 6, 12, 13],
          //Photos
          [MenuItem.PhotosMenuItem]: [2, 3, 6, 12, 13],
          // Keywords
          [MenuItem.KeywordsMenuItem]: [2, 6, 12],
          // Notices
          [MenuItem.NoticesMenuItem]: [2, 3, 12, 13],
          //"Connection Requests"
          [MenuItem.ConnectionRequests]: [2, 3, 6, 12, 13],
          //Reporting
          [MenuItem.Reporting]: [2, 12],
        };

        // Check if the MenuName exists in the allowedRoles object and if the user's role is included in the allowed roles for that menu
        return (
          allowedRoles[menu.managementMenuID]?.includes(
            managementUserData.UserRoleID
          ) || false
        );
      });

    dispatch(managementUserAction.CreateManagementUser(addUser)).then((res) => {
      const successMessages = {
        1: { text: `User Information saved successfully`, icon: "success" },
        2: { text: `User Information Updated successfully`, icon: "success" },
        "-1": { text: `Operation Failed`, icon: "error" },
        "-2": { text: `Email Already Exists`, icon: "error" },
        "-3": { text: `User name Already Exists`, icon: "error" },
      };
      if (res?.payload.IsSucceed === 1) {
        // Insert Management User Activity Log
        // dispatch(managementUserAction.resetManagementUserSearchData());
        dispatch(
          authActions?.ManagementUserActivityLog({
            ManagementUserID: user?.ManagementUserID,
            facilityID: FacilityID,
            ProcedureName: StoredProcedures?.MangementUserCPU,
            UserAction: `Created a New Management User (UserName - ${addUser?.Username})`,
          })
        );
      } else if (res?.payload.IsSucceed === 2) {
        // dispatch(managementUserAction.resetManagementUserSearchData());
        // Insert Management User Activity Log
        dispatch(
          authActions?.ManagementUserActivityLog({
            ManagementUserID: user?.ManagementUserID,
            facilityID: FacilityID,
            ProcedureName: StoredProcedures?.MangementUserCPU,
            UserAction: `Updated Details in Management User (ManagementUserID - ${addUser?.ManagementUserID})`,
          })
        );
      }
      const message = successMessages[res.payload.IsSucceed];
      const isSuccessful = [1, 2].includes(res.payload.IsSucceed);
      if (message) {
        if (isSuccessful) {
          history.navigate("/Admin/ManagementUserList");
          dispatch(managementUserAction.resetForm());
        }
        showToastNotification(message.text, message.icon);
      }
    });
  };

  //Reset
  const handleReset = () => {
    // Clear error messages
    setErrorMessage({});

    // Reset additional fields or state (e.g., hide extra fields)
    setShowExtraFeild(false);

    // Remove input-error class from all input fields
    const errorElements = document.querySelectorAll(".input-error");
    errorElements.forEach((element) => {
      element.classList.remove("input-error");
    });

    // Dispatch reset action to clear form data
    dispatch(managementUserAction.resetMenuList());
    dispatch(managementUserAction.resetForm());
  };

  return (
    <div>
      <div className="d-flex flex-wrap flex-column justify-content-center align-items-center position-relative">
        <div className="card shadow-sm b-r-all p-4 border border-dark-grey mt-3 w-100 mb-2">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <h2 className="form_title_uppercase text-color">ADD USER</h2>
            <Button
              variant="primary"
              className="me-2"
              label={"Back"}
              type={"button"}
              status={"primary"}
              onClick={handleBack}
              icon={"fa-solid fa-angle-left"}
              disabled={false}
            />
          </div>

          <ManagementUserForm
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            showExtraFeild={showExtraFeild}
            setShowExtraFeild={setShowExtraFeild}
            isEdit={false}
          />
          <FacilityAdminMenuAccess />
          <div className="d-flex flex-wrap justify-content-center mt-3 gap-3">
            <Button
              label="Save"
              status="primary"
              type="submit"
              onClick={handleSubmit}
              icon={"fa-solid fa-floppy-disk"}
            />
            <Button
              label="Reset"
              status="secondary"
              icon={"fa-solid fa-rotate-right"}
              type="button"
              onClick={handleReset}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(AddManagementUser);
